import { Autocomplete } from '@react-google-maps/api'
import cn from 'classnames'
import { AnimationProps, motion } from 'framer-motion'
import { observer } from 'mobx-react'
import React, { useId, useRef, useState } from 'react'

import {
   AsyncComboBox,
   colors,
   ComboboxContextProvider,
   CustomOptionType,
   Item,
   TextField
} from '@flowwai-frontend/design-system'
import {
   Delete02Icon,
   MapPin02Icon,
   SearchMdIcon,
   XCloseIcon
} from '@flowwai-frontend/design-system/icons'
import {
   EMPTY_STRING,
   ENTER_KEY_VALUE
} from '@flowwai-frontend/shared/constants'
import {
   validateLatitude,
   validateLongitude
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { MAPS_NO_DATA_LOCATION } from '../../../constants'
import {
   API_CONTENT_TYPE,
   API_METHOD,
   MAP_CENTER_POINT,
   RADIUS_IN_METERS
} from '../../../constants/OlaMapsAutocompleteConstants'
import { useMapsUtilityContext } from '../../../hooks/useMapsUtilityContext'
import { MapCoordinates } from '../../../types'
import { getOlaMapSearchUrl } from '../../../utils/OlaMapsAutocompleteUtil'

import * as Styles from './styles'

interface ResponseData {
   mainText: string
   secondaryText: string
   location: {
      lng: number
      lat: number
   }
   referenceId: string
}

interface Props {
   onSearchLocation: (coordinates: MapCoordinates) => void
   className?: string
   alwaysExpanded?: boolean
   animationProps?: AnimationProps
}

const PlacesSearchTool = (props: Props): React.ReactElement => {
   const [isExpanded, setIsExpanded] = useState(props.alwaysExpanded ?? false)
   const [inputValue, setInputValue] = useState('')
   const [responseData, setResponseData] = useState<ResponseData[]>([])

   const {
      mapsT,
      serviceProvidersConfig: { search: searchServiceProviderConfig }
   } = useMapsUtilityContext()

   const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

   const requestId = useId()

   const olaMapSearchApi = async (
      page: number,
      filterText?: string
   ): Promise<Array<ResponseData>> => {
      if (!filterText?.length) return []
      try {
         const url = getOlaMapSearchUrl(
            MAP_CENTER_POINT.latitude,
            MAP_CENTER_POINT.longitude,
            RADIUS_IN_METERS,
            filterText
         )
         const response = await fetch(url, {
            method: API_METHOD,
            headers: {
               'Content-Type': API_CONTENT_TYPE,
               'X-Request-Id': requestId
            }
         })

         if (response.ok) {
            const data = await response.json()
            const responseData = data.predictions.map((each: any) => ({
               mainText: each.structured_formatting.main_text,
               secondaryText: each.structured_formatting.secondary_text,
               location: each.geometry.location,
               referenceId: each.reference
            }))

            setResponseData(responseData)
            return responseData
         }
         return []
      } catch (error) {
         return []
      }
   }

   const onLoadAutoComplete = (
      autocomplete: google.maps.places.Autocomplete
   ): void => {
      autoCompleteRef.current = autocomplete
   }

   const renderCustomOption = (props: CustomOptionType<ResponseData>) => {
      const { item, optionProps, optionRef } = props
      const { value } = item

      const optionText = `${value.mainText}, ${value.secondaryText}`

      return (
         <div
            className={cn(Styles.placesSearchCustomOption)}
            ref={optionRef}
            {...optionProps}
         >
            <MapPin02Icon height={20} width={20} />
            <p className={cn(Styles.placesSearchOptionText)} title={optionText}>
               {optionText}
            </p>
         </div>
      )
   }

   const onSelectionChange = (key: React.Key) => {
      const selectedData = responseData.find(data => data.referenceId === key)
      if (selectedData) {
         setInputValue(selectedData.mainText)
         props.onSearchLocation(selectedData.location)
      }
   }

   const testCoordinatesInputAndAddCoordinates = (
      inputString: string
   ): boolean => {
      const [latString, lngString] = inputString.split(',')

      const trimmedLatString = latString?.trim()

      const latitudeValue = trimmedLatString ? Number(trimmedLatString) : null

      const isValidLatitude = latitudeValue
         ? validateLatitude(latitudeValue)
         : false

      const trimmedLngString = lngString?.trim()

      const longitudeValue = trimmedLngString ? Number(trimmedLngString) : null

      const isValidLongitude = longitudeValue
         ? validateLongitude(longitudeValue)
         : false

      const isValidLatAndLng = isValidLatitude && isValidLongitude
      if (!isValidLatAndLng) return false

      props.onSearchLocation({ lat: latitudeValue!, lng: longitudeValue! })
      return true
   }

   const onPlaceChanged = () => {
      if (!autoCompleteRef.current || !inputValue.trim()) return

      const areCoordinatesAdded = testCoordinatesInputAndAddCoordinates(
         inputValue
      )
      if (areCoordinatesAdded) return

      try {
         const place = autoCompleteRef.current.getPlace()

         setInputValue(place.name || '')

         const location = place.geometry?.location
         if (!location) return

         props.onSearchLocation({
            lat: location.lat(),
            lng: location.lng()
         })
      } catch (err) {
         console.warn(err)
      }
   }

   const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      const isEnterKey = e.key === ENTER_KEY_VALUE

      if (!isEnterKey) return

      testCoordinatesInputAndAddCoordinates(e.currentTarget.value)
   }

   const renderClearIcon = (): React.ReactNode =>
      inputValue ? (
         <button onClick={() => setInputValue(EMPTY_STRING)}>
            <Delete02Icon />
         </button>
      ) : null

   const renderPlacesSearchCombobox = (): React.ReactElement => {
      switch (searchServiceProviderConfig?.serviceProvider) {
         case 'GOOGLE_MAPS': {
            return (
               <Autocomplete
                  onLoad={onLoadAutoComplete}
                  onPlaceChanged={onPlaceChanged}
                  className={cn('w-full')}
                  options={{
                     bounds: new google.maps.LatLngBounds(MAPS_NO_DATA_LOCATION)
                  }}
               >
                  <TextField
                     size='ExtraSmall'
                     placeholder={mapsT('olaMapSearchInput.placeholder')}
                     value={inputValue}
                     onChange={setInputValue}
                     inputContainerClassName='flex gap-2 !p-0 !pl-2 border-none shadow-none'
                     onKeyDown={onKeyDown}
                     focusRingVariant='None'
                  />
               </Autocomplete>
            )
         }
         case 'OLA_MAPS': {
            return (
               <ComboboxContextProvider>
                  <AsyncComboBox
                     placeholder={mapsT('olaMapSearchInput.placeholder')}
                     apiProps={{
                        apiFunction: olaMapSearchApi,
                        isInfiniteScrollable: true
                     }}
                     size={'ExtraSmall'}
                     customOption={renderCustomOption}
                     onSelectionChange={onSelectionChange}
                     inputValue={inputValue}
                     onInputChange={setInputValue}
                     popoverStyles={{
                        padding: '10px',
                        width: '360px',
                        left: '24px',
                        top: '64px'
                     }}
                     alwaysShowWebView={true}
                     onKeyDown={onKeyDown}
                     prefixEnhancer={() => <></>}
                     className='!border-none'
                     inputClassName='!p-0 -ml-[6px]'
                     focusRingVariant='None'
                     hideChevronIcon
                     shouldRenderClearIcon={false}
                     autoFocus={!props.alwaysExpanded}
                     menuTrigger={inputValue ? 'focus' : 'input'}
                  >
                     {option => (
                        <Item
                           textValue={option.mainText as string}
                           key={option.referenceId}
                        >
                           {option.mainText}
                        </Item>
                     )}
                  </AsyncComboBox>
               </ComboboxContextProvider>
            )
         }
         default:
            return <></>
      }
   }

   const onToggleExpand = () => {
      if (props.alwaysExpanded) return
      setIsExpanded(prev => !prev)
   }

   return (
      <motion.div
         className={cn(
            'bg-base-white rounded-full flex items-center p-3 shadow-lg',
            props.className,
            isExpanded ? 'w-[360px]' : 'cursor-pointer'
         )}
         {...props.animationProps}
         onClick={isExpanded ? undefined : onToggleExpand}
      >
         <button
            onClick={e => {
               e.stopPropagation()
               onToggleExpand()
            }}
         >
            <SearchMdIcon height={20} width={20} fill={colors['gray-800']} />
         </button>

         {isExpanded ? (
            <>
               {renderPlacesSearchCombobox()}

               {renderClearIcon()}

               {props.alwaysExpanded ? null : (
                  <>
                     <div className='h-[14px] w-px bg-gray-300 mx-2' />
                     <button
                        className='pr-1'
                        onClick={() => setIsExpanded(prev => !prev)}
                     >
                        <XCloseIcon
                           height={16}
                           width={16}
                           fill={colors['gray-500']}
                        />
                     </button>
                  </>
               )}
            </>
         ) : null}
      </motion.div>
   )
}

export default observer(PlacesSearchTool)
